import type { JSX, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { api } from "./api.js";
import { cn } from "./lib/utils.js";
import { useRealtime, type RealtimeStatus } from "./realtime.js";
import { Button } from "./components/ui/button.js";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./components/ui/sheet.js";
import { Dashboard } from "./pages/Dashboard.js";
import { Accounts } from "./pages/Accounts.js";
import { Transactions } from "./pages/Transactions.js";
import { StocksDashboard } from "./pages/StocksDashboard.js";
import { StocksPositions } from "./pages/StocksPositions.js";
import { StocksSlots } from "./pages/StocksSlots.js";
import { StocksPrices } from "./pages/StocksPrices.js";
import { StocksSecurities } from "./pages/StocksSecurities.js";
import { StocksPerformance } from "./pages/StocksPerformance.js";
import { StockDetail } from "./pages/StockDetail.js";
import { StocksNews } from "./pages/StocksNews.js";
import { Categories } from "./pages/Categories.js";
import { Budgets } from "./pages/Budgets.js";
import { Retirement } from "./pages/Retirement.js";
import { Documents } from "./pages/Documents.js";
import { DataLogs } from "./pages/DataLogs.js";
import { Settings } from "./pages/Settings.js";
import { SettingsCatalog } from "./pages/SettingsCatalog.js";
import { SettingsIntegrations } from "./pages/SettingsIntegrations.js";
import { Login } from "./pages/Login.js";
import { CurrencySelector } from "./components/CurrencySelector.js";

export function App(): JSX.Element {
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me() });
  if (me.isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!me.data?.user) return <Login />;
  return <AuthedApp email={me.data.user.email} />;
}

function NavItem({
  to,
  end,
  child,
  children,
}: {
  to: string;
  end?: boolean;
  child?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        cn(
          "block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
          child && "pl-9 text-[13px]",
          isActive ? "bg-accent font-medium text-foreground" : "text-foreground/80",
        )
      }
    >
      {children}
    </NavLink>
  );
}

function SidebarContent({
  email,
  status,
}: {
  email: string;
  status: RealtimeStatus;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col p-4">
      <h1 className="mb-4 px-2 text-base font-semibold">Finance OS</h1>
      <nav className="flex flex-col gap-0.5">
        <NavItem to="/" end>
          Dashboard
        </NavItem>
        <NavItem to="/accounts">Accounts</NavItem>
        <NavItem to="/transactions">Transactions</NavItem>
        <NavItem to="/stocks" end>
          Stocks
        </NavItem>
        <NavItem to="/stocks/dashboard" child>
          Dashboard
        </NavItem>
        <NavItem to="/stocks/positions" child>
          Positions
        </NavItem>
        <NavItem to="/stocks/performance" child>
          Performance
        </NavItem>
        <NavItem to="/stocks/slots" child>
          Slots
        </NavItem>
        <NavItem to="/stocks/prices" child>
          Prices
        </NavItem>
        <NavItem to="/stocks/securities" child>
          Securities
        </NavItem>
        <NavItem to="/stocks/news" child>
          News
        </NavItem>
        <NavItem to="/budgets">Budgets</NavItem>
        <NavItem to="/retirement">Retirement</NavItem>
        <NavItem to="/documents">Documents</NavItem>
        <NavItem to="/data" end>
          Data
        </NavItem>
        <NavItem to="/data/logs" child>
          Logs
        </NavItem>
        <NavItem to="/settings" end>
          Settings
        </NavItem>
        <NavItem to="/settings/catalog" child>
          Catalog
        </NavItem>
        <NavItem to="/settings/integrations" child>
          Integrations
        </NavItem>
        <NavItem to="/categories" child>
          Categories
        </NavItem>
      </nav>
      <div className="mt-auto flex flex-col gap-1 pt-4 text-xs text-muted-foreground">
        <CurrencySelector />
        <RealtimeIndicator status={status} />
        <span className="truncate px-1 py-1">{email}</span>
        <Button
          variant="outline"
          size="sm"
          className="mt-1 w-full"
          onClick={() => {
            void api.logout().then(() => {
              window.location.reload();
            });
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}

function AuthedApp({ email }: { email: string }): JSX.Element {
  const status = useRealtime();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar (md+) */}
      <aside className="bg-panel hidden w-56 shrink-0 flex-col overflow-y-auto border-r md:flex">
        <SidebarContent email={email} status={status} />
      </aside>

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar with hamburger (< md) */}
        <header className="bg-panel flex items-center gap-3 border-b px-4 py-2.5 md:hidden">
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0" aria-describedby={undefined}>
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <SidebarContent email={email} status={status} />
            </SheetContent>
          </Sheet>
          <span className="font-semibold">Finance OS</span>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/accounts" element={<Accounts />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/stocks" element={<Navigate to="/stocks/dashboard" replace />} />
            <Route path="/stocks/dashboard" element={<StocksDashboard />} />
            <Route path="/stocks/positions" element={<StocksPositions />} />
            <Route path="/stocks/performance" element={<StocksPerformance />} />
            <Route path="/stocks/s/:securityId" element={<StockDetail />} />
            <Route path="/stocks/slots" element={<StocksSlots />} />
            <Route path="/stocks/prices" element={<StocksPrices />} />
            <Route path="/stocks/securities" element={<StocksSecurities />} />
            <Route path="/stocks/news" element={<StocksNews />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/budgets" element={<Budgets />} />
            <Route path="/retirement" element={<Retirement />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/data" element={<Navigate to="/data/logs" replace />} />
            <Route path="/data/logs" element={<DataLogs />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/catalog" element={<SettingsCatalog />} />
            <Route path="/settings/integrations" element={<SettingsIntegrations />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function RealtimeIndicator({ status }: { status: RealtimeStatus }): JSX.Element {
  const map: Record<RealtimeStatus, { dot: string; label: string; className: string }> = {
    idle: { dot: "○", label: "starting", className: "text-muted-foreground" },
    connecting: { dot: "○", label: "connecting…", className: "text-muted-foreground" },
    live: { dot: "●", label: "live", className: "text-good" },
    reconnecting: { dot: "◐", label: "reconnecting…", className: "text-warn" },
    offline: { dot: "×", label: "offline", className: "text-bad" },
  };
  const s = map[status];
  return (
    <div className={cn("flex items-center gap-1.5 px-1 text-[11px]", s.className)}>
      <span className="text-sm">{s.dot}</span>
      <span>{s.label}</span>
    </div>
  );
}
